import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, ViewChild } from '@angular/core';
import { DocumentAction, FirstToFileWidgetEventKeys, FirstToFileWidgetExports, FirstToFileWidgetFeatureFlags, FirstToFileWidgetInstance, FirstToFileWidgetType, FirstToFileWidgetXProps, IPCase, IPTypes, OrientationType, TrifoldFilters } from '@model/widget.model';

/**
 * If zoid & widget scripts were included in html, then a global scoped factory method is defined
 * that can be used to create a new widget instance.
 */
declare const FirstToFileWidget: FirstToFileWidgetType;

@Component({
  selector: 'ftf-widget',
  templateUrl: './ftf-widget.component.html',
  styleUrls: ['./ftf-widget.component.scss'],
})
export class WidgetComponent implements AfterViewInit, OnDestroy, FirstToFileWidgetExports {

  private instance!: FirstToFileWidgetInstance;
  private _initialized = false;
  private _productName?: string;
  private _orientation?: OrientationType;
  private _featureFlags?: FirstToFileWidgetFeatureFlags;
  private _events!: { [key in FirstToFileWidgetEventKeys]: boolean };

  @Input()
  get productName(): string | undefined { return this._productName; }
  set productName(value: string | undefined) {
    if (this._productName === value) return;
    this._productName = value;
    // if the widget is already rendered, update the props
    this.instance?.updateProps({ productName: value });
  }
  // get & set for _events
  @Input()
  get events(): { [key in FirstToFileWidgetEventKeys]: boolean } { return this._events; }
  set events(value: { [key in FirstToFileWidgetEventKeys]: boolean }) {
    this._events = value;
  }

  @Input()
  get orientation(): OrientationType | undefined {
    return this._orientation;
  }
  set orientation(value: OrientationType | undefined) {
    if (this._orientation === value) return;
    this._orientation = value;
    // if you try to update props with an undefined, it doesn't go through so we'll use null
    this.instance?.updateProps({ orientation: value === undefined ? null : value });
  }
  @Input()
  get featureFlags(): FirstToFileWidgetFeatureFlags | undefined {
    return this._featureFlags;
  }
  set featureFlags(value: FirstToFileWidgetFeatureFlags | undefined) {
    if (JSON.stringify(this.featureFlags ?? {}) === JSON.stringify(value ?? {})) return;
    this._featureFlags = value;
    this.instance?.updateProps({ featureFlags: value === undefined ? null : value });
  }


  @Output() documentAction = new EventEmitter<DocumentAction>();
  @Output() widgetLoad = new EventEmitter<void>();
  @Output() authAction = new EventEmitter<void>();
  @Output() caseOpen = new EventEmitter<IPCase>();
  @Output() commandAction = new EventEmitter<{ command: keyof FirstToFileWidgetExports, args: [] }>();

  // All of the properties as a single object
  get props(): FirstToFileWidgetXProps {
    const props: FirstToFileWidgetXProps = {
      productName: this._productName,
      onLoad: () => {
        if (this._initialized) {
          // if we reach this condition it means the widget iframe got refreshed somehow
          // and it will have broken the export handlers so we need to reinitialize widget
          console.log('widget reload detected.  reinitializing widget');
          this.instance?.close();
          this.initWidget();
        } else {
          if (this._events.onLoad) this.widgetLoad.emit()
          this._initialized = true;
        }
      },
      onAuth: () => { if (this._events.onAuth) this.authAction.emit() },
      onCaseOpen: (item) => { if (this._events.onCaseOpen) this.caseOpen.emit(item) },
      onCommand: (command, args) => { if (this._events.onCommand) this.commandAction.emit({ command, args }) },
      onDocumentAction: (doc) => { if (this._events.onDocumentAction) this.documentAction.emit(doc) },
    };
    if (this._orientation !== null && this._orientation !== undefined) {
      props.orientation = this._orientation;
    }
    if (this._featureFlags !== null && this._featureFlags !== undefined) {
      props.featureFlags = this._featureFlags;
    }
    return props;
  }

  @ViewChild('widgetContainer') widgetContainer!: ElementRef;

  constructor(private zone: NgZone) {}

  ngOnDestroy(): void {
    this.instance?.close();
  }

  ngAfterViewInit(): void {
    this.initWidget();
  }

  initWidget() {
    this.zone.runOutsideAngular(() => {
      this._initialized = false;
      this.widgetContainer.nativeElement.innerHTML = '';
      this.instance = FirstToFileWidget(this.props);
      this.instance.render(this.widgetContainer.nativeElement);
    });
  }

  downloadDocument(id: number, name: string, type: IPTypes) {
    return this.instance.downloadDocument(id, name, type);
  }

  openByCaseRefNum(caseReference: string, type?: 'Patent' | 'Trademark' | 'Matter', filters?: TrifoldFilters) {
    return this.instance.openByCaseRefNum(caseReference, type, filters);
  }

  openByAppSerNum(serialNo: string, type?: 'Patent' | 'Trademark' | 'Matter', filters?: TrifoldFilters) {
    return this.instance.openByAppSerNum(serialNo, type, filters);
  }

}
